import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Link2, ShieldAlert } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { api, errMessage } from "../lib/api";

const EXPIRY_OPTIONS = [
  { hours: 24, label: "24 hours" },
  { hours: 168, label: "7 days" },
  { hours: 720, label: "30 days" },
];

// Parent-only: create/revoke a private, expiring, view-only story share link.
export const ShareStoryDialog = ({ story }) => {
  const [open, setOpen] = useState(false);
  const [existing, setExisting] = useState(null);
  const [expiresHours, setExpiresHours] = useState(168);
  const [pin, setPin] = useState("");
  const [usePin, setUsePin] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLink(null);
    setNeedsConfirm(false);
    setConfirmed(false);
    api.get(`/story/${story.story_id}/share`)
      .then(({ data }) => setExisting(data.share))
      .catch(() => setExisting(null));
  }, [open, story.story_id]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/story/${story.story_id}/share`, {
        expires_hours: expiresHours,
        pin: usePin && pin ? pin : null,
        confirm_personal: confirmed,
      });
      setLink(`${window.location.origin}${data.path}`);
      setExisting(null);
      toast.success("Private share link created");
    } catch (e) {
      if (e.response?.data?.detail === "CONFIRM_PERSONAL_REQUIRED") {
        setNeedsConfirm(true);
      } else {
        toast.error(errMessage(e));
      }
    } finally {
      setCreating(false);
    }
  };

  const revoke = async () => {
    try {
      await api.delete(`/story/${story.story_id}/share`);
      setExisting(null);
      setLink(null);
      toast.success("Share link revoked — it no longer works");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the link manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="shrink-0 gap-1 text-muted-foreground h-8"
          data-testid={`share-story-${story.story_id}`}>
          <Link2 className="h-3.5 w-3.5" aria-hidden="true" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="share-story-dialog">
        <DialogHeader>
          <DialogTitle>Share "{story.title}"</DialogTitle>
          <DialogDescription>
            Create a private, view-only link for family. Anyone who has the link can view this
            story until it expires — only share it with people you trust.
          </DialogDescription>
        </DialogHeader>

        {existing && !link && (
          <div className="rounded-xl border border-border p-3 space-y-2" data-testid="share-existing">
            <p className="text-sm font-semibold">A share link is already active</p>
            <p className="text-xs text-muted-foreground">
              Expires {new Date(existing.expires_at).toLocaleString()} · viewed {existing.view_count} time{existing.view_count === 1 ? "" : "s"}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-full flex-1"
                onClick={() => copy(`${window.location.origin}/shared/${existing.token}`)} data-testid="share-copy-existing">
                {copied ? <Check className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5 mr-1" aria-hidden="true" />}
                Copy link
              </Button>
              <Button variant="outline" size="sm" className="rounded-full text-destructive border-destructive/40"
                onClick={revoke} data-testid="share-revoke-button">Revoke</Button>
            </div>
            <p className="text-xs text-muted-foreground">Creating a new link below replaces this one.</p>
          </div>
        )}

        {link ? (
          <div className="space-y-3" data-testid="share-created">
            <div className="flex gap-2">
              <Input readOnly value={link} className="text-xs" data-testid="share-link-input" />
              <Button size="sm" className="rounded-lg shrink-0" onClick={() => copy(link)} data-testid="share-copy-button">
                {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              View-only · no account needed · not indexed by search engines · you can revoke it anytime from this dialog.
            </p>
            <Button variant="outline" size="sm" className="rounded-full w-full text-destructive border-destructive/40"
              onClick={revoke} data-testid="share-revoke-new-button">Revoke this link</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold mb-2">Link expires after</p>
              <div className="flex gap-2">
                {EXPIRY_OPTIONS.map((o) => (
                  <button key={o.hours} type="button" onClick={() => setExpiresHours(o.hours)}
                    className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold border transition-colors ${expiresHours === o.hours ? "bg-secondary text-secondary-foreground border-secondary" : "bg-card border-border hover:bg-accent"}`}
                    data-testid={`share-expiry-${o.hours}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                <Checkbox checked={usePin} onCheckedChange={(v) => setUsePin(Boolean(v))} data-testid="share-pin-toggle" />
                Protect with a PIN (optional)
              </label>
              {usePin && (
                <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  inputMode="numeric" placeholder="4-8 digit PIN" className="h-10" data-testid="share-pin-input" />
              )}
            </div>

            {needsConfirm && (
              <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="share-personal-warning">
                <p className="text-sm font-semibold flex items-center gap-2 text-amber-800">
                  <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" /> This story mentions your child's name
                </p>
                <label className="flex items-start gap-2 text-xs text-amber-900 cursor-pointer">
                  <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(Boolean(v))}
                    className="mt-0.5" data-testid="share-personal-confirm" />
                  I understand the story contains my child's name and I still want to share it with this private link.
                </label>
              </div>
            )}

            <Button className="w-full rounded-full" onClick={create}
              disabled={creating || (usePin && pin.length < 4) || (needsConfirm && !confirmed)}
              data-testid="share-create-button">
              {creating ? "Creating…" : "Create private link"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
